import type { Atom, WritableAtom } from 'jotai';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

export interface PaginationConfig<TParams, TPayload, TLoadMoreParams, TState = unknown> {
    collectionStateFamily: (params: TParams) => Atom<TState>;
    loadMoreAtom: WritableAtom<unknown, [TLoadMoreParams], Promise<void>>;
    getParams: (payload: TPayload) => TParams & { fetchLimit?: number };
    getLoadMoreParams?: (params: TParams & { fetchLimit?: number }, fetchLimit: number) => TLoadMoreParams;
    entityType?: string;
}

export function usePagination<TParams, TPayload, TLoadMoreParams = TParams, TState = unknown>(
    payload: TPayload,
    config: PaginationConfig<TParams, TPayload, TLoadMoreParams, TState>
) {
    const { collectionStateFamily, loadMoreAtom, getParams, getLoadMoreParams } = config;

    const params = getParams(payload);
    const { fetchLimit = 20 } = params;

    // Get state from the collection family
    const state = useAtomValue(collectionStateFamily(params)) as {
        ids: string[];
        status: LoadStatus;
        hasMore: boolean;
        error?: Error | null;
    };
    const { ids, status, hasMore, error } = state;
    const triggerLoadMore = useSetAtom(loadMoreAtom);

    const loadMore = useCallback(async () => {
        if (status === 'loading' || !hasMore) return false;
        const loadMoreParams = getLoadMoreParams ? getLoadMoreParams(params, fetchLimit) : ({ ...params, fetchLimit } as TLoadMoreParams);
        await triggerLoadMore(loadMoreParams);
        return true;
    }, [status, hasMore, triggerLoadMore, params, fetchLimit, getLoadMoreParams]);

    return {
        ids,
        loadMore,
        hasMore,
        loadStatus: status,
        error,
    };
}
